// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Builds ReactFlow node type registry from registered pipeline nodes.
 *
 * Wraps each node component with PipelineNodeProvider to auto-inject
 * fromApp/isExtension flags from node data. Node authors don't need
 * to manually pass these props.
 */
import { memo, type ComponentType } from 'react'
import type { NodeTypes, NodeProps, Node } from '@xyflow/react'
import { getAllPipelineNodes } from '../../../core/pipeline-registry'
import type { PipelineNodeData } from '../../../core/contracts'
import { PipelineNodeProvider } from '../context'

type AnyNode = Node<PipelineNodeData>
type AnyNodeComponent = ComponentType<NodeProps<AnyNode>>

/**
 * HOC that wraps a WorkflowNode with PipelineNodeProvider.
 * Extracts common props from node data and props, provides via context.
 * Node authors don't need to pass: fromApp, isExtension, instanceName, configured, status, selected
 */
function withPipelineNodeContext(WrappedComponent: AnyNodeComponent): AnyNodeComponent {
  const WithContext = memo((props: NodeProps<AnyNode>) => {
    const { data, selected } = props
    const { fromApp, isExtension, label, configured, status } = data ?? {}
    return (
      <PipelineNodeProvider value={{
        fromApp,
        isExtension,
        instanceName: label,
        configured,
        status,
        selected,
      }}>
        <WrappedComponent {...props} />
      </PipelineNodeProvider>
    )
  })
  WithContext.displayName = `WithPipelineNodeContext(${(WrappedComponent as { displayName?: string }).displayName ?? 'Component'})`
  return WithContext
}

export const buildPipelineNodeTypes = (): NodeTypes =>
  Object.fromEntries(
    getAllPipelineNodes().map(node => [
      node.id,
      withPipelineNodeContext(node.WorkflowNode as AnyNodeComponent) as NodeTypes[string],
    ])
  )
